import { DecimalPipe } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { trackByValue } from '@freelancer/ui/helpers';
import { IconColor, IconModule, IconSize } from '@freelancer/ui/icon';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import { isDefined } from '@freelancer/utils';
import type { Subscription } from 'rxjs';
import {
  RatingColor,
  RatingTextColor,
  RatingTicksColor,
  RatingType,
} from './rating.types';

/** Path data from assets/icons/ui-star.svg (fill controlled via CSS). */
const UI_STAR_PATH =
  'M6.758 7.643 5.46 11.55l-.551-.402-.563-.402-.564-.402-.563-.403L0 7.643zm8.436 6.115-.241-.747-.219-.656.161-.114.104-.07 1.873-1.344.046-.035 4.402-3.149h-8.138l-.103-.299-.287-.885-.011-.034L10.666 0 8.643 6.137 5.93 14.344l-.011.034-1.85 5.62 4.574-3.275.793-.563.655-.472.575-.413.39.287.173.127 1.023.735 5 3.575z';

@Component({
  standalone: true,
  imports: [DecimalPipe, IconModule, ValidationErrorComponent],
  selector: 'fl-rating',
  template: `
    <div
      class="RatingContainer {{
        control.invalid && control.dirty ? 'mb-xxxsmall' : ''
      }}"
    >
      <div
        class="LayerGroup mr-xxxsmall"
        [attr.aria-disabled]="isInteractable ? control.disabled : undefined"
        [attr.aria-errormessage]="isInteractable ? errorId : undefined"
        [attr.aria-invalid]="isInteractable ? control.invalid : undefined"
        [attr.aria-label]="
          translateRating(control.value | number: ratingValueFormat)
        "
        [attr.data-background-color]="backgroundColor"
        [attr.data-compact]="compact"
        [attr.data-compact-tablet]="compactTablet"
        [attr.data-compact-desktop-small]="compactDesktopSmall"
        [attr.data-compact-desktop-large]="compactDesktopLarge"
        [attr.data-rating]="control.value"
        [attr.data-read-only]="readOnly"
        [attr.role]="isInteractable ? 'group' : 'img'"
        [attr.tabindex]="!isInteractable ? 0 : undefined"
        (mouseleave)="setHover(0)"
        (focusout)="setHover(0)"
      >
        <!-- RATING ICONS -->
        @if (
          compact === false ||
          compactTablet === false ||
          compactDesktopSmall === false ||
          compactDesktopLarge === false
        ) {
          <div
            class="IconLayer"
            [attr.data-compact]="compact"
            [attr.data-compact-tablet]="compactTablet"
            [attr.data-compact-desktop-small]="compactDesktopSmall"
            [attr.data-compact-desktop-large]="compactDesktopLarge"
          >
            @for (
              item of items;
              track trackByValue(itemIndex, item);
              let itemIndex = $index;
              let isLast = $last
            ) {
              <div
                class="IconContainer IconContainerSvg"
                [class.IconSpacer]="!isLast"
                [attr.data-type]="type"
                [attr.data-ticks-color]="ticksColor"
              >
                <!-- RatingType STARS — left-half + full layers enable half-star hover/value -->
                @if (type === RatingType.STARS) {
                  <svg
                    class="StarIcon StarIcon-leftHalf"
                    width="11"
                    height="20"
                    viewBox="0 0 11 20"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    [class.IsActive]="
                      isDefined(control.value)
                        ? control.value >= item - 0.75
                        : false
                    "
                    [class.IsHover]="hoverValue >= item - 0.75"
                    [class.IsInteractable]="isInteractable"
                    [attr.data-size]="size"
                    [attr.data-size-tablet]="sizeTablet"
                    [attr.data-size-desktop]="sizeDesktop"
                    [attr.data-size-desktop-large]="sizeDesktopLarge"
                    [attr.tabindex]="isInteractable && halfRating ? 0 : -1"
                    [attr.aria-label]="
                      isInteractable && halfRating
                        ? ratingTexts[itemIndex * 2]
                        : undefined
                    "
                    [attr.role]="
                      isInteractable && halfRating ? 'checkbox' : 'presentation'
                    "
                    [attr.focusable]="isInteractable && halfRating"
                    [attr.aria-checked]="
                      isInteractable && halfRating
                        ? isDefined(control.value) &&
                          control.value >= item - 0.5
                        : undefined
                    "
                    (click)="setValue(halfRating ? item - 0.5 : item)"
                    (mouseover)="setHover(halfRating ? item - 0.5 : item)"
                    (focusin)="setHover(halfRating ? item - 0.5 : item)"
                  >
                    <!-- Clipped to the left half of ui-star.svg via viewBox -->
                    <path [attr.d]="uiStarPath" />
                  </svg>
                  <svg
                    class="StarIcon StarIcon-full"
                    width="22"
                    height="20"
                    viewBox="0 0 22 20"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    [class.IsActive]="
                      isDefined(control.value)
                        ? control.value >= item - 0.25
                        : false
                    "
                    [class.IsHover]="hoverValue >= item - 0.25"
                    [class.IsInteractable]="isInteractable"
                    [attr.data-size]="size"
                    [attr.data-size-tablet]="sizeTablet"
                    [attr.data-size-desktop]="sizeDesktop"
                    [attr.data-size-desktop-large]="sizeDesktopLarge"
                    [attr.tabindex]="isInteractable ? 0 : -1"
                    [attr.aria-label]="
                      isInteractable
                        ? ratingTexts[itemIndex * 2 + 1]
                        : undefined
                    "
                    [attr.focusable]="isInteractable"
                    [attr.role]="isInteractable ? 'checkbox' : 'presentation'"
                    [attr.aria-checked]="
                      isInteractable
                        ? isDefined(control.value) &&
                          control.value >= item - 0.25
                        : undefined
                    "
                    (click)="setValue(item)"
                    (mouseover)="setHover(item)"
                    (focusin)="setHover(item)"
                  >
                    <path [attr.d]="uiStarPath" />
                  </svg>
                }
                <!-- RatingType TICKS -->
                @if (type === RatingType.TICKS) {
                  <fl-icon
                    class="Ticks"
                    [class.IsActive]="
                      isDefined(control.value) ? control.value >= item : false
                    "
                    [class.IsHover]="hoverValue >= item"
                    [name]="'ui-check-in-circle-v2'"
                    [color]="IconColor.INHERIT"
                    [size]="size"
                    [sizeTablet]="sizeTablet"
                    [sizeDesktop]="sizeDesktop"
                    [sizeDesktopLarge]="sizeDesktopLarge"
                    [label]="
                      isInteractable ? ratingTexts[itemIndex * 2] : undefined
                    "
                    [attr.aria-checked]="
                      isInteractable
                        ? isDefined(control.value) && control.value >= item
                        : undefined
                    "
                    [useIconFont]="true"
                    [fillIconFont]="true"
                    (click)="setValue(item)"
                    (mouseover)="setHover(item)"
                    (focusin)="setHover(item)"
                  ></fl-icon>
                }
              </div>
            }
          </div>
        }

        <!-- COMPACT ICON -->
        @if (
          compact || compactTablet || compactDesktopSmall || compactDesktopLarge
        ) {
          <div
            class="IconLayerCompact"
            [attr.data-compact]="compact"
            [attr.data-compact-tablet]="compactTablet"
            [attr.data-compact-desktop-small]="compactDesktopSmall"
            [attr.data-compact-desktop-large]="compactDesktopLarge"
          >
            <div
              class="IconContainer"
              [attr.data-type]="type"
              [attr.data-ticks-color]="ticksColor"
            >
              @if (type === RatingType.TICKS) {
                <fl-icon
                  class="CompactIcon"
                  [class.IsActive]="control.value"
                  [name]="'ui-check-in-circle-v2'"
                  [color]="IconColor.INHERIT"
                  [size]="size"
                  [sizeTablet]="sizeTablet"
                  [sizeDesktop]="sizeDesktop"
                  [sizeDesktopLarge]="sizeDesktopLarge"
                  [useIconFont]="true"
                  [fillIconFont]="true"
                ></fl-icon>
              } @else {
                <svg
                  class="CompactIcon"
                  width="22"
                  height="20"
                  viewBox="0 0 22 20"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  [class.IsActive]="control.value"
                  [attr.data-size]="size"
                  [attr.data-size-tablet]="sizeTablet"
                  [attr.data-size-desktop]="sizeDesktop"
                  [attr.data-size-desktop-large]="sizeDesktopLarge"
                >
                  <path [attr.d]="uiStarPath" />
                </svg>
              }
            </div>
          </div>
        }
      </div>
      @if (showCount) {
        <div
          class="ValueBlock {{ isReviewCountSet ? 'mr-xxxsmall' : 'mr-none' }}"
          [attr.data-compact]="
            type === RatingType.TICKS && compact !== undefined
              ? compact
              : undefined
          "
          [attr.data-compact-tablet]="
            type === RatingType.TICKS && compactTablet !== undefined
              ? compactTablet
              : undefined
          "
          [attr.data-compact-desktop-small]="
            type === RatingType.TICKS && compactDesktopSmall !== undefined
              ? compactDesktopSmall
              : undefined
          "
          [attr.data-compact-desktop-large]="
            type === RatingType.TICKS && compactDesktopLarge !== undefined
              ? compactDesktopLarge
              : undefined
          "
          [attr.data-icon-size]="size"
          [attr.data-icon-size-tablet]="sizeTablet"
          [attr.data-icon-size-desktop]="sizeDesktop"
          [attr.data-icon-size-desktop-large]="sizeDesktopLarge"
          [attr.data-text-color]="textColor"
        >
          {{ itemsCount | number: ratingValueFormat }}
        </div>
      }
      @if (isReviewCountSet) {
        <div
          class="ReviewCount"
          [attr.data-icon-size]="size"
          [attr.data-icon-size-tablet]="sizeTablet"
          [attr.data-icon-size-desktop]="sizeDesktop"
          [attr.data-icon-size-desktop-large]="sizeDesktopLarge"
          [attr.data-text-color]="textColor"
        >
          @if (reviewTextSingular && reviewTextPlural) {
            @if (reviewCount === 1) {
              ({{ reviewCount }} {{ reviewTextSingular }})
            }
            @if (reviewCount !== 1) {
              ({{ reviewCount }} {{ reviewTextPlural }})
            }
          } @else {
            @if (reviewCount === 1) {
              <ng-container i18n="Single number of review">
                ({{ reviewCount }} review)
              </ng-container>
            }
            @if (reviewCount !== 1) {
              <ng-container i18n="Number of reviews">
                ({{ reviewCount }} reviews)
              </ng-container>
            }
          }
        </div>
      }
    </div>
    <fl-validation-error
      [id]="errorId"
      [control]="control"
    ></fl-validation-error>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./rating.component.scss'],
})
export class RatingComponent implements OnDestroy, OnInit, OnChanges {
  changeDetectorRef = inject(ChangeDetectorRef);

  IconColor = IconColor;
  RatingType = RatingType;

  isDefined = isDefined;
  trackByValue = trackByValue;
  uiStarPath = UI_STAR_PATH;

  readonly ratingValueFormat = '1.1-1';
  readonly ratingTexts: readonly string[] = [
    this.translateRating(0.5),
    this.translateRating(1),
    this.translateRating(1.5),
    this.translateRating(2),
    this.translateRating(2.5),
    this.translateRating(3),
    this.translateRating(3.5),
    this.translateRating(4),
    this.translateRating(4.5),
    this.translateRating(5),
  ];

  items: number[] = [1, 2, 3, 4, 5];
  hoverValue = 0;
  itemsCount = 0;
  isReviewCountSet: boolean;
  isInteractable: boolean;
  errorId: string;
  showCount: boolean;

  private valueChangeSubscription?: Subscription;

  // Accepts `undefined` as a default value, but internally, it
  // should never set `control.value` to `undefined`
  @Input() control: FormControl<number | undefined>;
  @Input() size = IconSize.MID;
  @Input() sizeTablet: IconSize;
  @Input() sizeDesktop: IconSize;
  @Input() sizeDesktopLarge: IconSize;
  @Input() halfRating = false;
  @Input() hideValue = false;
  @Input() reviewCount?: number;
  @Input() reviewTextSingular?: string;
  @Input() reviewTextPlural?: string;

  /** Display the component inline with other inline level elements */
  @HostBinding('attr.data-display-inline')
  @Input()
  inline = false;

  @HostBinding('attr.data-read-only')
  @Input()
  readOnly = false;

  @HostBinding('attr.data-icon-color')
  @Input()
  backgroundColor: RatingColor;

  @HostBinding('attr.data-compact')
  @Input()
  compact = false;

  /** Makes the component compact on sizes tablet and larger */
  @HostBinding('attr.data-compact-tablet')
  @Input()
  compactTablet?: boolean;

  /** Makes the component compact on sizes desktop small and larger */
  @HostBinding('attr.data-compact-desktop-small')
  @Input()
  compactDesktopSmall?: boolean;

  /** Makes the component compact on sizes desktop large and larger */
  @HostBinding('attr.data-compact-desktop-large')
  @Input()
  compactDesktopLarge?: boolean;

  @HostBinding('attr.data-type')
  @Input()
  type = RatingType.STARS;

  @HostBinding('attr.data-ticks-color')
  @Input()
  ticksColor = RatingTicksColor.PRIMARY;

  @HostBinding('attr.data-text-color')
  @Input()
  textColor: RatingTextColor = 'foreground';

  @Output() hoverValueChange = new EventEmitter<number>();

  ngOnInit(): void {
    this.errorId = `rating-error_${this.generateUniqueId()}`;
    this.itemsCount = this.control.value ?? 0;
    this.valueChangeSubscription = this.control.valueChanges.subscribe(() => {
      this.render();
    });
    this.showCount =
      (!this.hideValue && this.type !== RatingType.TICKS) ||
      (this.type === RatingType.TICKS &&
        !!(
          this.compact ||
          this.compactTablet ||
          this.compactDesktopSmall ||
          this.compactDesktopLarge
        ));
    this.setInteractableState();
    this.render();
  }

  ngOnDestroy(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }

  setValue(value: number): void {
    if (!this.isInteractable) {
      return;
    }
    this.hoverValue = 0;
    this.control.markAsDirty();
    this.control.setValue(value);
  }

  private render(): void {
    this.itemsCount = (this.hoverValue || this.control.value) ?? 0;
    this.changeDetectorRef.markForCheck();
  }

  setHover(value: number): void {
    if (!this.isInteractable) {
      return;
    }
    this.hoverValue = value;
    this.render();
    this.hoverValueChange.emit(this.hoverValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('reviewCount' in changes) {
      this.isReviewCountSet = isDefined(this.reviewCount);
    }

    if ('compact' in changes || 'readOnly' in changes) {
      this.setInteractableState();
    }
  }

  setInteractableState(): void {
    this.isInteractable = !this.readOnly;
  }

  translateRating(rating?: number | string | null): string {
    return isDefined(rating)
      ? $localize`Rating: ${rating} out of 5`
      : $localize`Unrated`;
  }

  /**
   * TODO: T281398 - Remove this once we have a proper unique id generator
   */
  private generateUniqueId(): string {
    return Math.random().toString(36).substring(2, 6);
  }
}
